import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpEventType } from '@angular/common/http';
import { BehaviorSubject, Subject } from 'rxjs';
import { LoadingController } from '@ionic/angular';
import { ConfigService } from '../config/config.service';
import { GlobalErrorHandlerService } from '../global-errors/global-error-handler.service';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@awesome-cordova-plugins/file-opener/ngx';
@Injectable({
	providedIn: 'root',
})

export class WebservicesService {

	private readyBS: BehaviorSubject<boolean> = new BehaviorSubject(false);

	constructor(
		private readonly globalErrorHandler: GlobalErrorHandlerService,
		private readonly httpClient: HttpClient,
		private readonly loadingCtrl: LoadingController,
		private fileOpener: FileOpener,
		private readonly config: ConfigService
	) {
		this.config.isLoaded().then(() => {
			this.readyBS.next(true);
		});
	}

	private getParamsObject(params: any) {
		let optionParams = new HttpParams();
		if (params) {
			for (let k in params) {
				if(params[k] !== undefined && params[k] !== null){
					optionParams = optionParams.set(k, params[k]);
				}
			}
		}
		return optionParams;
	}

	private getWebservicesURL() {
		return this.config.getEnv().webservices_url
	}

	private async showLoading(): Promise<HTMLIonLoadingElement> {
		const loadingDialog = await this.loadingCtrl.create({
			message: 'Please wait...',
		});
		loadingDialog.present();

		return loadingDialog;
	}

	get(method: string, params?: any, hideLoader = false) {
		let loadingTimer:any;
		let loadingDialogPromise: Promise<HTMLIonLoadingElement>;
		if (!hideLoader) {
			loadingTimer = setTimeout(() => {
				loadingDialogPromise = this.showLoading();
			}, 5000);
		}

		const responseSubject = new Subject<any>();
		const isReadySubscription = this.readyBS.subscribe(isReady => {
			if (isReady) {
				const optionParams = this.getParamsObject(params);

				this.httpClient
					.get(`${this.getWebservicesURL()}/${method}`, {
						params: optionParams,
						observe: 'body',
					})
					.subscribe({
						next: response => {
							if (response['result'] == 1) {
								responseSubject.next(response);
							} else {
								const error = this.globalErrorHandler.handleError(response);
								if (!error.isSuppressed()) {
									responseSubject.error(error);
								}
							}
							responseSubject.complete();
						},
						error: () => {
							const error = this.globalErrorHandler.handleError({ 'code': 'HTTP_ERROR' });
							if (!error.isSuppressed()) {
								responseSubject.error(error);
							}
							clearTimeout(loadingTimer);
							loadingDialogPromise?.then(loadingDialog => {
								loadingDialog.dismiss();
							});
						},
						complete: () => {
							clearTimeout(loadingTimer);
							loadingDialogPromise?.then(loadingDialog => {
								loadingDialog.dismiss();
							});
						}
					});
			} else {
				clearTimeout(loadingTimer);
				loadingDialogPromise?.then(loadingDialog => {
					loadingDialog.dismiss();
				});
			}
		});
		responseSubject.subscribe(() => {
			isReadySubscription.unsubscribe();
		});
		return responseSubject;
	}

	getXML(method: string, params?: any) {
		let loadingDialogPromise: Promise<HTMLIonLoadingElement>;
		const loadingTimer = setTimeout(() => {
			loadingDialogPromise = this.showLoading();
		}, 5000);
		const optionParams = this.getParamsObject(params);
		const responseSubject = new Subject<any>();
		this.httpClient
			.get(`${this.getWebservicesURL()}/${method}`, {
				params: optionParams,
				observe: 'body',
				responseType: 'text',
			})
			.subscribe({
				next: response => {
					responseSubject.next(response);
					responseSubject.complete();
				},
				error: () => {
					clearTimeout(loadingTimer);
					loadingDialogPromise?.then(loadingDialog => {
						loadingDialog.dismiss();
					});
					const error = this.globalErrorHandler.handleError({ 'code': 'HTTP_ERROR' });
					if (!error.isSuppressed()) {
						responseSubject.error(error);
					}
				},
				complete: () => {
					clearTimeout(loadingTimer);
					loadingDialogPromise?.then(loadingDialog => {
						loadingDialog.dismiss();
					});
				}
			});
		return responseSubject;
	}

	post(method: string, params?: any) {
		let loadingDialogPromise: Promise<HTMLIonLoadingElement>;
		const loadingTimer = setTimeout(() => {
			loadingDialogPromise = this.showLoading();
		}, 5000);

		const headers = {};

		headers['Content-Type'] = 'application/x-www-form-urlencoded';

		let body = new HttpParams();
		if (params) {
			for (const k in params) {
				if (params[k] !== undefined && params[k] !== null) {
					body = body.set(k, params[k]);
				}
			}
		}

		const responseSubject = new Subject<any>();
		this.httpClient
			.post(`${this.getWebservicesURL()}/${method}`, body, { headers })
			.subscribe({
				next: response => {
					if (response['result'] == 1) {
						responseSubject.next(response);
					} else {
						const error = this.globalErrorHandler.handleError(response);
						if (!error.isSuppressed()) {
							responseSubject.error(error);
						}
					}
					responseSubject.complete();
				},
				error: () => {
					const error = this.globalErrorHandler.handleError({ 'code': 'HTTP_ERROR' });
					if (!error.isSuppressed()) {
						responseSubject.error(error);
					}
					clearTimeout(loadingTimer);
					loadingDialogPromise?.then(loadingDialog => {
						loadingDialog.dismiss();
					});
				},
				complete: () => {
					clearTimeout(loadingTimer);
					loadingDialogPromise?.then(loadingDialog => {
						loadingDialog.dismiss();
					});
				}
			});

		return responseSubject;
	}

	/**  this post method will do just post api and will not handle any response or error */
	postNoError(method: string, params?: any) {
		const headers = {};
		headers['Content-Type'] = 'application/x-www-form-urlencoded';
		let body = new HttpParams();
		if (params) {
			for (const k in params) {
				body = body.set(k, params[k]);
			}
		}
		const responseSubject = new Subject<any>();
		this.httpClient
			.post(`${this.getWebservicesURL()}/${method}`, body, { headers })
			.subscribe({
				next: response => {
					if (response['result'] == 1) {
						responseSubject.next(response);
					} else {
						responseSubject.error(null);
					}
					responseSubject.complete();
				},
				error: err => {
					if (err) {
						responseSubject.error(err);
					}
				}
			});
		return responseSubject;
	}

	postFormData(method: string, body?: FormData) {
		let loadingDialogPromise: Promise<HTMLIonLoadingElement>;
		const loadingTimer = setTimeout(() => {
			loadingDialogPromise = this.showLoading();
		}, 5000);

		const options = {
			headers: new HttpHeaders(),
		};
		options.headers.set('Content-Type', 'multipart/form-data');

		const responseSubject = new Subject<any>();
		this.httpClient
			.post(`${this.getWebservicesURL()}/${method}`, body, options)
			.subscribe({
				next: response => {
					if (response['result'] == 1) {
						responseSubject.next(response);
					} else {
						const error = this.globalErrorHandler.handleError(response);
						if (!error.isSuppressed()) {
							responseSubject.error(error);
						}
					}
					responseSubject.complete();
				},
				error: () => {
					clearTimeout(loadingTimer);
					loadingDialogPromise?.then(loadingDialog => {
						loadingDialog.dismiss();
					});
					const error = this.globalErrorHandler.handleError({ 'code': 'HTTP_ERROR' });
					if (!error.isSuppressed()) {
						responseSubject.error(error);
					}
				},
				complete: () => {
					clearTimeout(loadingTimer);
					loadingDialogPromise?.then(loadingDialog => {
						loadingDialog.dismiss();
					});
				}
			});

		return responseSubject;
	}

	getApiNoRedirect(method: string, deviceType: string, parameters?: any) {
		const options = {
			headers: new HttpHeaders(),
			params: new HttpParams()
		}
		// if (parameters) {
		// for (const k in parameters) {
		//options.params = options.params.set(k, parameters[k]);
		// }
		// }

		if (deviceType != 'mobile') {
			options.headers = options.headers.append('random-x', parameters.session_token);
		}

		const httpSubject = new Subject<any>();

		this.httpClient.get(method, options).subscribe({
			next: (httpResp) => {
				if (httpResp['result'] == 1) {
					httpSubject.next(httpResp);
				} else {
					const error = this.globalErrorHandler.handleError(httpResp);
					if (!error.isSuppressed()) {
						httpSubject.error(error);
					}
				}
				httpSubject.complete();
			},
			error: () => {
				const error = this.globalErrorHandler.handleError({ 'code': 'HTTP_ERROR' });
				if (!error.isSuppressed()) {
					httpSubject.error(error);
				}
			}
		});

		return httpSubject;
	}

	download(downloadFileUrl, targetFileName) {
		// let downloadProgress = 0;
		let downloadFileName = "";
		let downloadBase64 = "";
		this.httpClient.get(downloadFileUrl, {
			responseType: 'blob',
			reportProgress: true,
			observe: 'events'
		}).subscribe(async event => {
			console.log(event);
			// if(event.type === HttpEventType.DownloadProgress){
			//   console.log("in DownloadProgress");
			//   if(event.total == undefined){
			// 	downloadProgress =0;
			//   } else {
			// 	downloadProgress = Math.round((100*event.loaded)/event.total);
			//   }
			// } else 
			if (event.type === HttpEventType.Response) {
				console.log("got download Response");
				//   downloadProgress =0;
				downloadFileName = downloadFileUrl.substring(downloadFileUrl.lastIndexOf('/') + 1);
				console.log("event:", event.body);
				if (event.body != null) {
					downloadBase64 = await this.convertBlobToBase64(event.body) as string;
					console.log(downloadBase64);
					const savedFile = await Filesystem.writeFile({
						path: targetFileName,//"winloss.csv",
						data: downloadBase64,
						directory: Directory.Documents,
					});

					console.log('saved ', savedFile);

					const path = savedFile.uri;
					const mimeType = this.getMimeType(downloadFileName);

					// this.fileOpener.open(path, mimeType)
					//   .then(()=>console.log('File is opened'))
					//   .catch(error=>console.log('error opening file', error));

					this.fileOpener.showOpenWithDialog(path, mimeType)
						.then(() => console.log("open file dialog"))
						.catch(e => console.log('can not open it', e))
				}
			}
		});
	}

	private convertBlobToBase64 = (blob: Blob) => new Promise((resolve, reject) => {
		const reader = new FileReader;
		reader.onerror = reject;
		reader.onload = () => {
			resolve(reader.result);
		};
		reader.readAsDataURL(blob);
	});

	private getMimeType(name: string) {
		if (name.indexOf("pdf") >= 0) {
			return 'application/pdf';
		} else if (name.indexOf("png") >= 0) {
			return 'image/png';
		} else if (name.indexOf("mp4") >= 0) {
			return 'video/mp4';
		} else if (name.indexOf("csv") >= 0) {
			return 'text/csv';
		} else {
			return "";
		}
	}
}
